import { html, LitElement, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";
import styles from './usos-banner.scss';
import "../icon/usos-icon";

/**
 * A lightweight, accessible banner component used to display
 * messages in a consistent way.
 *
 * @slot - banner content
 */
@customElement('usos-banner')
export abstract class UsosBanner extends LitElement {
    static override styles = styles;

    // section: properties

    /**
     * Defines the semantic type of the banner.
     * The value affects both the visual styling and the default icon.
     *
     * Available types:
     * - info — neutral informational messages
     * - notice — warnings or attention‑required messages
     * - success — confirmation of successful actions,
     * - error — information about unsuccessful actions
     *
     * For accessibility reasons, the `success` and `error` types
     * are rendered with `role="alert"`
     *
     * Default: 'info'
     */
    @property({reflect: true}) type: 'info' | 'notice' | 'success' | 'error' = 'info';

    /**
     * Overrides the default icon used for the banner.
     * If not provided, an icon appropriate for the current `type` is used.
     *
     * Note: overriding the icon is intended for exceptional cases only,
     * e.g. when a different symbol carries important semantic meaning
     * and improves user comprehension of the message.
     */
    @property({attribute: 'icon-name'}) iconName: Optional<string> = null;

    // section: state

    // section: extra fields

    // section: lifecycle

    override render() {
        return html`
            <div class="inline-infobox" role=${['success', 'error'].includes(this.type) ? 'alert' : nothing}>
                <usos-icon icon-name=${this._getIconName()}></usos-icon>
                <div>
                    <slot></slot>
                </div>
            </div>`;
    }

    // section: extra methods

    // section: event handlers

    // section: private methods

    /**
     * Returns the manually provided icon (if any),
     * otherwise falls back to the default icon for the current banner type.
     */
    private _getIconName(){
        return this.iconName ?? {
            info: 'outlined.info',
            notice: 'outlined.warning_amber',
            error: 'outlined.clear',
            success: 'outlined.check',
        }[this.type];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'usos-banner': UsosBanner;
    }
}
